import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, ShieldCheck, TrendingUp, Globe2 } from 'lucide-react';

const EvolutionTimeline = () => {
  const milestones = [
    { 
      year: "Jan 2022", 
      title: "Launch & Foundation", 
      description: "Started our journey with a simple mission: to make IT solutions accessible and high-performing for small and medium-sized businesses.", 
      icon: Rocket 
    },
    { 
      year: "March 2023", 
      title: "Security & Cloud Expansion", 
      description: "Expanded our offerings to include cybersecurity and cloud solutions, protecting client data while providing reliable infrastructure.", 
      icon: ShieldCheck 
    },
    { 
      year: "April 2024", 
      title: "Accelerated Growth", 
      description: "Reached an important milestone, supporting over 50+ businesses globally in their secure digital transformation.", 
      icon: TrendingUp 
    },
    { 
      year: "Sept 2025", 
      title: "Global Vision", 
      description: "Became a benchmark leader in innovative IT services, offering highly efficient solutions tailored to international enterprise needs.", 
      icon: Globe2 
    }
  ];

  return (
    <section className="py-24 px-6 max-w-6xl mx-auto overflow-hidden">
      
      {/* Section Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-20"
      >
        <span className="text-electric-green font-bold text-sm uppercase tracking-widest mb-2 block">
          Our Journey
        </span>
        <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight">
          The Evolution of ANESDEV
        </h2>
      </motion.div>

      {/* Timeline Container */}
      <div className="relative">
        
        {/* The Vertical Line */}
        <div className="absolute left-[27px] md:left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-white/10 to-transparent md:-translate-x-1/2 z-0" />

        {milestones.map((item, index) => {
          const isLeft = index % 2 === 0;
          const Icon = item.icon;

          return (
            <div key={index} className="relative w-full mb-12 group z-10">
              
              {/* CSS Grid guarantees the icon and cards will NEVER overlap on desktop */}
              <div className="flex flex-col md:grid md:grid-cols-[1fr_auto_1fr] md:gap-12 items-center w-full">

                {/* LEFT COLUMN (Only renders if it's a left-aligned item) */}
                {isLeft ? (
                  <motion.div 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="w-full pl-20 md:pl-0 md:text-right"
                  >
                    <div className="bg-[#1A1F29]/40 backdrop-blur-md border border-white/5 rounded-2xl p-8 hover:bg-[#1A1F29]/80 hover:border-white/10 transition-all duration-300">
                      <span className="inline-block px-4 py-1.5 mb-4 rounded-full bg-white/5 border border-white/10 text-electric-green text-xs font-bold tracking-widest uppercase">
                        {item.year}
                      </span>
                      <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-electric-green transition-colors duration-300">
                        {item.title}
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                ) : (
                  <div className="hidden md:block" /> /* Empty spacer for right-aligned items */
                )}

                {/* CENTER COLUMN (The Icon) */}
                <div className="absolute left-0 top-4 md:static flex items-center justify-center w-14 h-14 rounded-full bg-[#0B0E14] border-2 border-white/10 z-20 group-hover:border-electric-green group-hover:shadow-[0_0_20px_rgba(57,255,20,0.2)] transition-all duration-300">
                  <Icon className="w-6 h-6 text-gray-500 group-hover:text-electric-green transition-colors" />
                </div>

                {/* RIGHT COLUMN (Only renders if it's a right-aligned item) */}
                {!isLeft ? (
                  <motion.div 
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="w-full pl-20 md:pl-0 md:text-left"
                  >
                    <div className="bg-[#1A1F29]/40 backdrop-blur-md border border-white/5 rounded-2xl p-8 hover:bg-[#1A1F29]/80 hover:border-white/10 transition-all duration-300">
                      <span className="inline-block px-4 py-1.5 mb-4 rounded-full bg-white/5 border border-white/10 text-electric-green text-xs font-bold tracking-widest uppercase">
                        {item.year}
                      </span>
                      <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-electric-green transition-colors duration-300">
                        {item.title}
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                ) : (
                  <div className="hidden md:block" /> /* Empty spacer for left-aligned items */
                )}

              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default EvolutionTimeline;